import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { Users, Calendar, Clock, TrendingUp, CheckCircle, Plus, X, Edit, Trash2, RotateCcw, Activity, MessageSquare } from 'lucide-react';
import Papa from 'papaparse';
import type { ParseResult } from 'papaparse';
import { formatGusmesterNames } from '../utils/gusmesterDisplay';
import { AuthContext } from '../App';

// Only these accounts may create/delete sessions or change a session's schedule,
// capacity, publish status, or type — must mirror SESSION_MANAGER_USER_IDS in server/index.js.
const SESSION_MANAGER_USER_IDS = [1, 12]; // Admin, Lars S

interface Booking {
  id: number;
  session_date: string;
  start_time: string;
  end_time: string;
  gusmester_name: string;
  status: string;
  name: string;
  created_at: string;
}

interface Gusmester {
  id: number;
  name: string;
  email: string;
  phone: string;
  bio: string;
  picture_url: string;
  is_active: number;
  sessions_held: number;
}

interface Session {
  id: number;
  session_date: string;
  start_time: string;
  end_time: string;
  gusmester_id: number | null;
  gusmester_name: string | null;
  session_type: 'regular' | 'special_event';
  gusmesters?: { id: number; name: string; email: string }[];
  max_capacity: number;
  booked_count: number;
  waiting_count: number;
  name: string;
  description: string;
  released: boolean;
}

// Add User type
interface User {
  id: number;
  name: string;
  email: string;
  phonenr: string;
  is_admin: boolean;
  created_at: string;
}

const AdminDashboard: React.FC = () => {
  const { user } = useContext(AuthContext);
  const isSessionManager = !!user && SESSION_MANAGER_USER_IDS.includes(user.id);

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [gusmesters, setGusmesters] = useState<Gusmester[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  
  // Form state
  const [sessionDate, setSessionDate] = useState('');
  const [gusmesterId, setGusmesterId] = useState('');
  const [sessionType, setSessionType] = useState<'regular' | 'special_event'>('regular');
  const [gusmesterIds, setGusmesterIds] = useState<string[]>([]);
  const [maxCapacity, setMaxCapacity] = useState('16');
  const [sessionName, setSessionName] = useState('');
  const [sessionDescription, setSessionDescription] = useState('');
  const [startTime, setStartTime] = useState('20:00');
  const [endTime, setEndTime] = useState('21:00');
  const [released, setReleased] = useState(false);

  // Gusmester management state
  const [showGusmesterForm, setShowGusmesterForm] = useState(false);
  const [editingGusmester, setEditingGusmester] = useState<Gusmester | null>(null);
  const [gusmesterForm, setGusmesterForm] = useState({
    name: '',
    email: '',
    phone: '',
    bio: '',
    picture_url: ''
  });
  const [gusmesterLoading, setGusmesterLoading] = useState(false);
  const [gusmesterError, setGusmesterError] = useState('');
  const [gusmesterSuccess, setGusmesterSuccess] = useState('');

  // Session editing state
  const [showEditSessionForm, setShowEditSessionForm] = useState(false);
  const [editingSession, setEditingSession] = useState<Session | null>(null);
  // Add name and description to editSessionForm state
  const [editSessionForm, setEditSessionForm] = useState({
    sessionName: '',
    sessionDescription: '',
    sessionDate: '',
    startTime: '',
    endTime: '',
    gusmesterId: '',
    sessionType: 'regular' as 'regular' | 'special_event',
    gusmesterIds: [] as string[],
    maxCapacity: '',
    released: false
  });
  const [editSessionLoading, setEditSessionLoading] = useState(false);
  const [editSessionError, setEditSessionError] = useState('');
  const [editSessionSuccess, setEditSessionSuccess] = useState('');

  // Participants modal state
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [sessionParticipants, setSessionParticipants] = useState<Booking[]>([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);

  // Waiting list modal state
  const [showWaitingListModal, setShowWaitingListModal] = useState(false);
  const [sessionWaitingList, setSessionWaitingList] = useState<any[]>([]);
  const [waitingListLoading, setWaitingListLoading] = useState(false);

  // Add user management state
  const [users, setUsers] = useState<User[]>([]);
  const [userLoading, setUserLoading] = useState(true);
  const [userError, setUserError] = useState('');
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    phonenr: '',
    is_admin: false
  });
  const normalizeIdentifier = (value: string) => value.trim().toLowerCase();
  const [userFormLoading, setUserFormLoading] = useState(false);
  const [userFormError, setUserFormError] = useState('');
  const [userFormSuccess, setUserFormSuccess] = useState('');

  // Add state for import modal/result
  const [showImportModal, setShowImportModal] = useState(false);
  const [importResult, setImportResult] = useState<any[] | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');

  useEffect(() => {
    fetchBookings();
    fetchGusmesters();
    fetchSessions();
    fetchUsers();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get('/api/admin/bookings');
      setBookings(response.data);
    } catch (error) {
      setError('Kunne ikke indlæse bookinger');
    } finally {
      setLoading(false);
    }
  };

  const fetchGusmesters = async () => {
    try {
      const response = await axios.get('/api/admin/gusmesters');
      console.log('Gusmesters data:', response.data);
      setGusmesters(response.data);
    } catch (error) {
      console.error('Kunne ikke hente gusmestre:', error);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await axios.get('/api/admin/sessions');
      setSessions(response.data);
    } catch (error) {
      console.error('Kunne ikke hente sessioner:', error);
    }
  };

  const fetchSessionParticipants = async (sessionId: number) => {
    setParticipantsLoading(true);
    try {
      const response = await axios.get(`/api/admin/sessions/${sessionId}/participants`);
      setSessionParticipants(response.data);
    } catch (error) {
      console.error('Kunne ikke hente session deltagere:', error);
    } finally {
      setParticipantsLoading(false);
    }
  };

  const fetchSessionWaitingList = async (sessionId: number) => {
    setWaitingListLoading(true);
    try {
      const response = await axios.get(`/api/admin/sessions/${sessionId}/waiting-list`);
      setSessionWaitingList(response.data);
    } catch (error) {
      console.error('Kunne ikke hente venteliste:', error);
    } finally {
      setWaitingListLoading(false);
    }
  };

  const handleCreateSession = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!sessionDate) {
      setCreateError('Udfyld venligst session dato');
      return;
    }

    if (sessionType === 'special_event' && gusmesterIds.length === 0) {
      setCreateError('Vælg mindst én Gusmester til et special event');
      return;
    }

    setCreateLoading(true);
    setCreateError('');
    setCreateSuccess('');

    try {
      await axios.post('/api/sessions', {
        name: sessionName,
        description: sessionDescription,
        sessionDate,
        startTime,
        endTime,
        sessionType,
        gusmesterId: sessionType === 'regular' ? (gusmesterId ? parseInt(gusmesterId) : null) : null,
        gusmesterIds: sessionType === 'special_event' ? gusmesterIds.map(Number) : undefined,
        maxCapacity: parseInt(maxCapacity),
        released
      });

      setCreateSuccess('GUS session oprettet succesfuldt!');
      setSessionDate('');
      setGusmesterId('');
      setSessionType('regular');
      setGusmesterIds([]);
      setMaxCapacity('16');
      setShowCreateForm(false);
      
      // Refresh bookings to show the new session
      setTimeout(() => {
        fetchBookings();
      }, 1000);
      
    } catch (error: any) {
      setCreateError(error.response?.data?.error || 'Kunne ikke oprette session');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleGusmesterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!gusmesterForm.name || !gusmesterForm.email) {
      setGusmesterError('Navn og email er påkrævet');
      return;
    }

    setGusmesterLoading(true);
    setGusmesterError('');
    setGusmesterSuccess('');

    try {
      if (editingGusmester) {
        // Update existing gusmester
        await axios.put(`/api/gusmesters/${editingGusmester.id}`, gusmesterForm);
        setGusmesterSuccess('Gusmester opdateret succesfuldt!');
      } else {
        // Create new gusmester
        await axios.post('/api/gusmesters', gusmesterForm);
        setGusmesterSuccess('Gusmester oprettet succesfuldt!');
      }
      
      setGusmesterForm({ name: '', email: '', phone: '', bio: '', picture_url: '' });
      setEditingGusmester(null);
      setShowGusmesterForm(false);
      
      // Refresh gusmesters list
      setTimeout(() => {
        fetchGusmesters();
      }, 1000);
      
    } catch (error: any) {
      setGusmesterError(error.response?.data?.error || 'Kunne ikke gemme gusmester');
    } finally {
      setGusmesterLoading(false);
    }
  };

  const handleEditGusmester = (gusmester: Gusmester) => {
    setEditingGusmester(gusmester);
    setGusmesterForm({
      name: gusmester.name,
      email: gusmester.email,
      phone: gusmester.phone || '',
      bio: gusmester.bio || '',
      picture_url: gusmester.picture_url || ''
    });
    setShowGusmesterForm(true);
  };

  const handleToggleGusmesterActive = async (gusmester: Gusmester) => {
    const isHiding = gusmester.is_active !== 0;
    const confirmMessage = isHiding
      ? 'Er du sikker på, at du vil skjule denne Gusmester? De vil ikke længere blive vist på den offentlige side.'
      : 'Vil du vise denne Gusmester igen på den offentlige side?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await axios.patch(`/api/gusmesters/${gusmester.id}/active`, { is_active: !isHiding });
      setGusmesterSuccess(isHiding ? 'Gusmester skjult!' : 'Gusmester vises igen!');
      fetchGusmesters();
    } catch (error: any) {
      setGusmesterError(error.response?.data?.error || 'Kunne ikke opdatere gusmester');
    }
  };

  const resetGusmesterForm = () => {
    setGusmesterForm({ name: '', email: '', phone: '', bio: '', picture_url: '' });
    setEditingGusmester(null);
    setShowGusmesterForm(false);
    setGusmesterError('');
    setGusmesterSuccess('');
  };

  const handleEditSession = (session: Session) => {
    setEditingSession(session);
    setEditSessionForm({
      sessionName: session.name || '',
      sessionDescription: session.description || '',
      sessionDate: session.session_date,
      startTime: session.start_time,
      endTime: session.end_time,
      gusmesterId: session.gusmester_id ? session.gusmester_id.toString() : '',
      sessionType: session.session_type || 'regular',
      gusmesterIds: (session.gusmesters || []).map((g) => g.id.toString()),
      maxCapacity: session.max_capacity.toString(),
      released: session.released
    });
    setShowEditSessionForm(true);
  };

  const handleViewParticipants = async (session: Session) => {
    setSelectedSession(session);
    setShowParticipantsModal(true);
    await fetchSessionParticipants(session.id);
  };

  const handleViewWaitingList = async (session: Session) => {
    setSelectedSession(session);
    setShowWaitingListModal(true);
    await fetchSessionWaitingList(session.id);
  };

  const handleUpdateSession = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editSessionForm.sessionDate || !editSessionForm.startTime || !editSessionForm.endTime || !editSessionForm.sessionName) {
      setEditSessionError('Udfyld venligst session dato, start tid, slut tid og session navn');
      return;
    }

    if (editSessionForm.sessionType === 'special_event' && editSessionForm.gusmesterIds.length === 0) {
      setEditSessionError('Vælg mindst én Gusmester til et special event');
      return;
    }

    setEditSessionLoading(true);
    setEditSessionError('');
    setEditSessionSuccess('');

    try {
      await axios.put(`/api/sessions/${editingSession!.id}`, {
        name: editSessionForm.sessionName,
        description: editSessionForm.sessionDescription,
        sessionDate: editSessionForm.sessionDate,
        startTime: editSessionForm.startTime,
        endTime: editSessionForm.endTime,
        sessionType: editSessionForm.sessionType,
        gusmesterId: editSessionForm.sessionType === 'regular' ? (editSessionForm.gusmesterId ? parseInt(editSessionForm.gusmesterId) : null) : null,
        gusmesterIds: editSessionForm.sessionType === 'special_event' ? editSessionForm.gusmesterIds.map(Number) : undefined,
        maxCapacity: parseInt(editSessionForm.maxCapacity),
        released: editSessionForm.released
      });
      
      setEditSessionSuccess('GUS session opdateret succesfuldt!');
      setShowEditSessionForm(false);
      
      // Refresh sessions list
      setTimeout(() => {
        fetchSessions();
      }, 1000);
      
    } catch (error: any) {
      setEditSessionError(error.response?.data?.error || 'Kunne ikke opdatere session');
    } finally {
      setEditSessionLoading(false);
    }
  };

  const resetEditSessionForm = () => {
    setEditSessionForm({
      sessionName: '',
      sessionDescription: '',
      sessionDate: '',
      startTime: '',
      endTime: '',
      gusmesterId: '',
      sessionType: 'regular',
      gusmesterIds: [],
      maxCapacity: '',
      released: false
    });
    setEditingSession(null);
    setShowEditSessionForm(false);
    setEditSessionError('');
    setEditSessionSuccess('');
  };

  const handleDeleteSession = async () => {
    if (!editingSession) return;
    if (!window.confirm('Er du sikker på, at du vil slette denne session?')) return;
    try {
      await axios.delete(`/api/sessions/${editingSession.id}`);
      setShowEditSessionForm(false);
      fetchSessions();
    } catch (error) {
      alert('Kunne ikke slette sessionen. Prøv igen.');
    }
  };

  const getConfirmedBookings = () => {
    return bookings.filter(booking => booking.status === 'confirmed').length;
  };

  const getTodayBookings = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return bookings.filter(booking => booking.session_date === today).length;
  };

  const getUniqueMembers = () => {
    const uniqueNames = new Set(bookings.map(booking => booking.name));
    return uniqueNames.size;
  };

  const isInSeason = (dateString: string) => {
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    
    // Season: September 1st (month 9) to May 1st (month 5)
    if (month >= 9) return true; // September to December
    if (month <= 5 && day <= 1) return true; // January 1st to May 1st
    return false;
  };

  const getDayOfWeek = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, 'EEEE');
  };

  // Fetch users
  const fetchUsers = async () => {
    setUserLoading(true);
    setUserError('');
    try {
      const response = await axios.get('/api/admin/users');
      setUsers(response.data);
    } catch (error) {
      setUserError('Kunne ikke hente brugere');
    } finally {
      setUserLoading(false);
    }
  };

  // User form handlers
  const handleUserFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUserFormLoading(true);
    setUserFormError('');
    setUserFormSuccess('');
    const normalizedName = (userForm.name || '').trim();
    const normalizedEmail = normalizeIdentifier(userForm.email || '');

    if (!normalizedName || !normalizedEmail) {
      setUserFormError('Navn og email skal udfyldes.');
      setUserFormLoading(false);
      return;
    }

    try {
      if (editingUser) {
        // Edit user
        await axios.put(`/api/admin/users/${editingUser.id}`, {
          name: normalizedName,
          email: normalizedEmail,
          password: userForm.password || undefined,
          phonenr: userForm.phonenr,
          isAdmin: userForm.is_admin
        });
        setUserFormSuccess('Bruger opdateret!');
      } else {
        // Create user
        await axios.post('/api/admin/users', {
          name: normalizedName,
          email: normalizedEmail,
          password: userForm.password,
          phonenr: userForm.phonenr,
          isAdmin: userForm.is_admin
        });
        setUserFormSuccess('Bruger oprettet!');
      }
      setShowUserForm(false);
      setUserForm({ name: '', email: '', password: '', phonenr: '', is_admin: false });
      setEditingUser(null);
      fetchUsers();
    } catch (error: any) {
      setUserFormError(error.response?.data?.error || 'Kunne ikke gemme bruger');
    } finally {
      setUserFormLoading(false);
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setUserForm({
      name: user.name || '',
      email: normalizeIdentifier(user.email || ''),
      password: '',
      phonenr: user.phonenr || '',
      is_admin: user.is_admin
    });
    setShowUserForm(true);
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Er du sikker på, at du vil slette denne bruger?')) return;
    try {
      await axios.delete(`/api/admin/users/${userId}`);
      fetchUsers();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Kunne ikke slette bruger');
    }
  };

  const resetUserForm = () => {
    setUserForm({ name: '', email: '', password: '', phonenr: '', is_admin: false });
    setEditingUser(null);
    setShowUserForm(false);
    setUserFormError('');
    setUserFormSuccess('');
  };

  // Handle CSV import
  const handleImportCSV = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    setImportError('');
    setImportResult(null);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header: string) => header.trim().toLowerCase(),
      complete: async (results: ParseResult<any>) => {
        // Expect columns: name, email, password, phonenr/phone (optional), isAdmin (optional)
        const users = (results.data as any[]).map(row => ({
          name: row.name ? String(row.name).trim() : '',
          phonenr: row.phonenr || row.phone || null,
          email: row.email ? normalizeIdentifier(String(row.email)) : '',
          password: row.password,
          isAdmin: row.isadmin === 'true' || row.isadmin === true || row.isadmin === 1
        })).filter(u => u.name && u.email && u.password);
        if (users.length === 0) {
          setImportError('Ingen gyldige brugere fundet i CSV.');
          setImportLoading(false);
          return;
        }
        try {
          const response = await axios.post('/api/admin/users/bulk', { users });
          setImportResult(response.data.results);
          fetchUsers();
        } catch (err: any) {
          setImportError(err.response?.data?.error || 'Kunne ikke importere brugere');
        } finally {
          setImportLoading(false);
        }
      },
      error: () => {
        setImportError('Kunne ikke læse CSV filen.');
        setImportLoading(false);
      }
    });
  };

  // Max capacity is only relevant for regular sessions and only session managers may
  // change it. Publish status (released) is editable by any admin, except for special
  // events which can never be published.
  const capacityDisabled = editSessionForm.sessionType === 'special_event' || !isSessionManager;
  const statusDisabled = editSessionForm.sessionType === 'special_event';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Fejl</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchBookings}
            className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg"
          >
            Prøv Igen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">GUS Sauna Administration</h1>
              <p className="text-gray-600 mt-2">Administrer GUS sauna sessioner, gusmestre og bookinger</p>
            </div>
            <div className="flex gap-2">
              <Link
                to="/admin/logs"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
              >
                <Activity className="w-4 h-4 mr-2" />
                Se Log
              </Link>
              <Link
                to="/admin/sms"
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                SMS Beskeder
              </Link>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700 mx-auto"></div>
            <p className="mt-2 text-gray-600">Indlæser...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="w-6 h-6 text-blue-700" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Bookinger</p>
                <p className="text-2xl font-bold text-gray-900">{bookings.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Bekræftet</p>
                <p className="text-2xl font-bold text-gray-900">{getConfirmedBookings()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Dagens Bookinger</p>
                <p className="text-2xl font-bold text-gray-900">{getTodayBookings()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Aktive Medlemmer</p>
                <p className="text-2xl font-bold text-gray-900">{getUniqueMembers()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Gusmester Management Section */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Gusmester Administration</h2>
            <button
              onClick={() => setShowGusmesterForm(true)}
              className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Tilføj Gusmester
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '900px' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Navn
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Telefon
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Afholdt Gus
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Billede
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Handlinger
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {gusmesters.map((gusmester) => (
                  <tr key={gusmester.id} className={`hover:bg-gray-50 ${gusmester.is_active === 0 ? 'bg-gray-50 opacity-60' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium text-gray-900 ${gusmester.is_active === 0 ? 'line-through' : ''}`}>
                        {gusmester.name}
                        {gusmester.is_active === 0 && (
                          <span className="ml-2 text-xs font-normal text-gray-500 no-underline">(skjult)</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap min-w-0">
                      <div className="text-sm text-gray-900">
                        {gusmester.email ? (
                          <a href={`mailto:${gusmester.email}`} className="text-blue-600 hover:text-blue-800 underline">
                            {gusmester.email}
                          </a>
                        ) : (
                          <span className="text-gray-500">Ingen email</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap min-w-0">
                      <div className="text-sm text-gray-900">
                        {gusmester.phone ? (
                          <a href={`tel:${gusmester.phone}`} className="text-blue-600 hover:text-blue-800 underline">
                            {gusmester.phone}
                          </a>
                        ) : (
                          <span className="text-gray-500">Ingen telefon</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {gusmester.sessions_held}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {gusmester.picture_url ? (
                          <span className="text-green-600">✓</span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditGusmester(gusmester)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="Rediger"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleGusmesterActive(gusmester)}
                          className={gusmester.is_active === 0 ? "text-green-600 hover:text-green-900 p-1" : "text-red-600 hover:text-red-900 p-1"}
                          title={gusmester.is_active === 0 ? "Vis igen" : "Skjul"}
                        >
                          {gusmester.is_active === 0 ? <RotateCcw className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Sessions Management Section */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">GUS Session Administration</h2>
            {isSessionManager && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Opret Session
              </button>
            )}
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dato
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tid
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gusmester
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kapacitet
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Handlinger
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sessions
                  .sort((a, b) => new Date(a.session_date).getTime() - new Date(b.session_date).getTime())
                  .map((session) => (
                  <tr key={session.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium ${session.session_type === 'special_event' ? 'text-blue-700' : 'text-gray-900'}`}>
                        {format(new Date(session.session_date), 'EEEE, MMMM d, yyyy')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${session.session_type === 'special_event' ? 'text-blue-700' : 'text-gray-900'}`}>
                        {session.start_time} - {session.end_time}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${session.session_type === 'special_event' ? 'text-blue-700 font-medium' : 'text-gray-900'}`}>
                        {session.session_type === 'special_event' ? (
                          session.gusmesters && session.gusmesters.length > 0 ? (
                            session.gusmesters.length === 1 ? (
                              session.gusmesters[0].name
                            ) : (
                              <span>
                                {session.gusmesters[0].name}{' '}
                                <span
                                  className="cursor-help underline decoration-dotted"
                                  title={formatGusmesterNames(session.gusmesters.slice(1).map((g) => g.name))}
                                >
                                  og...
                                </span>
                              </span>
                            )
                          ) : (
                            'Ingen Gusmester'
                          )
                        ) : (
                          session.gusmester_name
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {session.booked_count}/{session.max_capacity}
                        {session.waiting_count > 0 && (
                          <span className="text-sky-700 text-xs ml-1">
                            ({session.waiting_count} venteliste)
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {session.released ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            Frigivet
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                            Ikke frigivet
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewParticipants(session)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="Se Deltagere"
                        >
                          <Users className="w-4 h-4" />
                        </button>
                        {session.waiting_count > 0 && (
                          <button
                            onClick={() => handleViewWaitingList(session)}
                            className="text-blue-700 hover:text-blue-900 p-1"
                            title="Se Venteliste"
                          >
                            <Clock className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEditSession(session)}
                          className="text-green-600 hover:text-green-900 p-1"
                          title="Rediger"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create Session Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-900">Opret GUS Session</h3>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="overflow-y-auto flex-1 px-6 py-4">
                {createError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                    {createError}
                  </div>
                )}

                {createSuccess && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
                    {createSuccess}
                  </div>
                )}

                <form onSubmit={handleCreateSession}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Navn
                  </label>
                  <input
                    type="text"
                    value={sessionName}
                    onChange={(e) => setSessionName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kort Beskrivelse
                  </label>
                  <textarea
                    value={sessionDescription}
                    onChange={(e) => setSessionDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={4}
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Dato
                  </label>
                  <input
                    type="date"
                    value={sessionDate}
                    onChange={(e) => setSessionDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Tid (24t format)
                  </label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Brug 24-timers format, f.eks. 18:00
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slut Tid (24t format)
                  </label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Brug 24-timers format, f.eks. 21:00
                  </p>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Type
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="sessionType"
                        value="regular"
                        checked={sessionType === 'regular'}
                        onChange={() => setSessionType('regular')}
                        className="mr-2 text-blue-700 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Regulær (én Gusmester)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="sessionType"
                        value="special_event"
                        checked={sessionType === 'special_event'}
                        onChange={() => { setSessionType('special_event'); setReleased(false); }}
                        className="mr-2 text-blue-700 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Special Event (flere Gusmestre)</span>
                    </label>
                  </div>
                </div>

                {sessionType === 'regular' ? (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gusmester
                    </label>
                    <select
                      value={gusmesterId}
                      onChange={(e) => setGusmesterId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Ingen Gusmester (valgfrit)</option>
                      {gusmesters.filter((gusmester) => gusmester.is_active !== 0).map((gusmester) => (
                        <option key={gusmester.id} value={gusmester.id}>
                          {gusmester.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gusmestre (vælg mindst én)
                    </label>
                    <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-3">
                      {gusmesters.filter((gusmester) => gusmester.is_active !== 0).map((gusmester) => (
                        <label key={gusmester.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={gusmesterIds.includes(gusmester.id.toString())}
                            onChange={(e) => {
                              const idStr = gusmester.id.toString();
                              setGusmesterIds((prev) =>
                                e.target.checked ? [...prev, idStr] : prev.filter((v) => v !== idStr)
                              );
                            }}
                            className="mr-2 text-blue-700 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{gusmester.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <label className={`block text-sm font-medium mb-2 ${sessionType === 'special_event' ? 'text-gray-400' : 'text-gray-700'}`}>
                    Maksimalt antal deltagere
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={maxCapacity}
                    onChange={(e) => setMaxCapacity(e.target.value)}
                    disabled={sessionType === 'special_event'}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${sessionType === 'special_event' ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                    placeholder="16"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {sessionType === 'special_event'
                      ? 'Ikke relevant for special events'
                      : 'Standard er 16 deltagere. Maksimum er 50.'}
                  </p>
                </div>

                <div className="mb-6">
                  <label className={`block text-sm font-medium mb-2 ${sessionType === 'special_event' ? 'text-gray-400' : 'text-gray-700'}`}>
                    Status
                  </label>
                  <div className="space-y-2">
                    <label className={`flex items-center ${sessionType === 'special_event' ? 'cursor-not-allowed' : ''}`}>
                      <input
                        type="radio"
                        name="released"
                        value="true"
                        checked={released === true}
                        onChange={(e) => setReleased(e.target.value === 'true')}
                        disabled={sessionType === 'special_event'}
                        className="mr-2 text-blue-700 focus:ring-blue-500"
                      />
                      <span className={`text-sm ${sessionType === 'special_event' ? 'text-gray-400' : 'text-gray-700'}`}>Frigivet (synlig på sessionssiden)</span>
                    </label>
                    <label className={`flex items-center ${sessionType === 'special_event' ? 'cursor-not-allowed' : ''}`}>
                      <input
                        type="radio"
                        name="released"
                        value="false"
                        checked={released === false}
                        onChange={(e) => setReleased(e.target.value === 'true')}
                        disabled={sessionType === 'special_event'}
                        className="mr-2 text-blue-700 focus:ring-blue-500"
                      />
                      <span className={`text-sm ${sessionType === 'special_event' ? 'text-gray-400' : 'text-gray-700'}`}>Ikke frigivet (kun synlig for admin)</span>
                    </label>
                  </div>
                  {sessionType === 'special_event' && (
                    <p className="text-xs text-gray-500 mt-1">Special events kan ikke frigives/publiceres.</p>
                  )}
                </div>

                  <div className="flex space-x-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowCreateForm(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Annuller
                    </button>
                    <button
                      type="submit"
                      disabled={createLoading}
                      className="flex-1 bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      {createLoading ? 'Opretter...' : 'Opret Session'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Gusmester Form Modal */}
        {showGusmesterForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingGusmester ? 'Rediger Gusmester' : 'Tilføj Gusmester'}
                </h3>
                <button
                  onClick={resetGusmesterForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {gusmesterError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                  {gusmesterError}
                </div>
              )}

              {gusmesterSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
                  {gusmesterSuccess}
                </div>
              )}

              <form onSubmit={handleGusmesterSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Navn
                    </label>
                    <input
                      type="text"
                      value={gusmesterForm.name}
                      onChange={(e) => setGusmesterForm({...gusmesterForm, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={gusmesterForm.email}
                      onChange={(e) => setGusmesterForm({...gusmesterForm, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    value={gusmesterForm.phone}
                    onChange={(e) => setGusmesterForm({...gusmesterForm, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bio
                  </label>
                  <textarea
                    value={gusmesterForm.bio}
                    onChange={(e) => setGusmesterForm({...gusmesterForm, bio: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={4}
                    placeholder="Skriv en kort beskrivelse af Gusmesteren..."
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Billede URL
                  </label>
                  <input
                    type="url"
                    value={gusmesterForm.picture_url}
                    onChange={(e) => setGusmesterForm({...gusmesterForm, picture_url: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com/image.jpg"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Indtast URL til et billede af Gusmesteren (valgfrit)
                  </p>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={resetGusmesterForm}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Annuller
                  </button>
                  <button
                    type="submit"
                    disabled={gusmesterLoading}
                    className="flex-1 bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    {gusmesterLoading ? 'Gemmer...' : (editingGusmester ? 'Opdater' : 'Tilføj')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Session Modal */}
        {showEditSessionForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-900">Rediger GUS Session</h3>
                <button
                  onClick={resetEditSessionForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="overflow-y-auto flex-1 px-6 py-4">
                {editSessionError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                    {editSessionError}
                  </div>
                )}

                {editSessionSuccess && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
                    {editSessionSuccess}
                  </div>
                )}

                <form onSubmit={handleUpdateSession}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Navn
                  </label>
                  <input
                    type="text"
                    value={editSessionForm.sessionName}
                    onChange={e => setEditSessionForm({ ...editSessionForm, sessionName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kort Beskrivelse
                  </label>
                  <textarea
                    value={editSessionForm.sessionDescription}
                    onChange={e => setEditSessionForm({ ...editSessionForm, sessionDescription: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={4}
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${!isSessionManager ? 'text-gray-400' : 'text-gray-700'}`}>
                    Session Dato
                  </label>
                  <input
                    type="date"
                    value={editSessionForm.sessionDate}
                    onChange={(e) => setEditSessionForm({...editSessionForm, sessionDate: e.target.value})}
                    disabled={!isSessionManager}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isSessionManager ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${!isSessionManager ? 'text-gray-400' : 'text-gray-700'}`}>
                    Start Tid
                  </label>
                  <input
                    type="time"
                    value={editSessionForm.startTime}
                    onChange={(e) => setEditSessionForm({...editSessionForm, startTime: e.target.value})}
                    disabled={!isSessionManager}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isSessionManager ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${!isSessionManager ? 'text-gray-400' : 'text-gray-700'}`}>
                    Slut Tid
                  </label>
                  <input
                    type="time"
                    value={editSessionForm.endTime}
                    onChange={(e) => setEditSessionForm({...editSessionForm, endTime: e.target.value})}
                    disabled={!isSessionManager}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isSessionManager ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className={`block text-sm font-medium mb-2 ${!isSessionManager ? 'text-gray-400' : 'text-gray-700'}`}>
                    Session Type
                  </label>
                  <div className="space-y-2">
                    <label className={`flex items-center ${!isSessionManager ? 'cursor-not-allowed' : ''}`}>
                      <input
                        type="radio"
                        name="editSessionType"
                        value="regular"
                        checked={editSessionForm.sessionType === 'regular'}
                        onChange={() => setEditSessionForm({ ...editSessionForm, sessionType: 'regular' })}
                        disabled={!isSessionManager}
                        className="mr-2 text-blue-700 focus:ring-blue-500"
                      />
                      <span className={`text-sm ${!isSessionManager ? 'text-gray-400' : 'text-gray-700'}`}>Regulær (én Gusmester)</span>
                    </label>
                    <label className={`flex items-center ${!isSessionManager ? 'cursor-not-allowed' : ''}`}>
                      <input
                        type="radio"
                        name="editSessionType"
                        value="special_event"
                        checked={editSessionForm.sessionType === 'special_event'}
                        onChange={() => setEditSessionForm({ ...editSessionForm, sessionType: 'special_event', released: false })}
                        disabled={!isSessionManager}
                        className="mr-2 text-blue-700 focus:ring-blue-500"
                      />
                      <span className={`text-sm ${!isSessionManager ? 'text-gray-400' : 'text-gray-700'}`}>Special Event (flere Gusmestre)</span>
                    </label>
                  </div>
                  {!isSessionManager && (
                    <p className="text-xs text-gray-500 mt-1">Kun Admin og Lars S kan ændre dato, tid og session type.</p>
                  )}
                </div>

                {editSessionForm.sessionType === 'regular' ? (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gusmester
                    </label>
                    <select
                      value={editSessionForm.gusmesterId}
                      onChange={(e) => setEditSessionForm({...editSessionForm, gusmesterId: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Ingen Gusmester (valgfrit)</option>
                      {gusmesters
                        .filter((gusmester) => gusmester.is_active !== 0 || String(gusmester.id) === editSessionForm.gusmesterId)
                        .map((gusmester) => (
                          <option key={gusmester.id} value={gusmester.id}>
                            {gusmester.name}{gusmester.is_active === 0 ? ' (skjult)' : ''}
                          </option>
                        ))}
                    </select>
                  </div>
                ) : (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gusmestre (vælg mindst én)
                    </label>
                    <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-3">
                      {gusmesters
                        .filter((gusmester) => gusmester.is_active !== 0 || editSessionForm.gusmesterIds.includes(gusmester.id.toString()))
                        .map((gusmester) => (
                        <label key={gusmester.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editSessionForm.gusmesterIds.includes(gusmester.id.toString())}
                            onChange={(e) => {
                              const idStr = gusmester.id.toString();
                              setEditSessionForm((prev) => ({
                                ...prev,
                                gusmesterIds: e.target.checked
                                  ? [...prev.gusmesterIds, idStr]
                                  : prev.gusmesterIds.filter((v) => v !== idStr)
                              }));
                            }}
                            className="mr-2 text-blue-700 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{gusmester.name}{gusmester.is_active === 0 ? ' (skjult)' : ''}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <label className={`block text-sm font-medium mb-2 ${capacityDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                    Maksimalt antal deltagere
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={editSessionForm.maxCapacity}
                    onChange={(e) => setEditSessionForm({...editSessionForm, maxCapacity: e.target.value})}
                    disabled={capacityDisabled}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${capacityDisabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                    placeholder="16"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {editSessionForm.sessionType === 'special_event'
                      ? 'Ikke relevant for special events'
                      : !isSessionManager
                      ? 'Kun Admin og Lars S kan ændre kapacitet'
                      : 'Standard er 16 deltagere. Maksimum er 50.'}
                  </p>
                </div>

                <div className="mb-6">
                  <label className={`block text-sm font-medium mb-2 ${statusDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                    Status
                  </label>
                  <div className="space-y-2">
                    <label className={`flex items-center ${statusDisabled ? 'cursor-not-allowed' : ''}`}>
                      <input
                        type="radio"
                        name="editReleased"
                        value="true"
                        checked={editSessionForm.released === true}
                        onChange={(e) => setEditSessionForm({...editSessionForm, released: e.target.value === 'true'})}
                        disabled={statusDisabled}
                        className="mr-2 text-blue-700 focus:ring-blue-500"
                      />
                      <span className={`text-sm ${statusDisabled ? 'text-gray-400' : 'text-gray-700'}`}>Frigivet (synlig på sessionssiden)</span>
                    </label>
                    <label className={`flex items-center ${statusDisabled ? 'cursor-not-allowed' : ''}`}>
                      <input
                        type="radio"
                        name="editReleased"
                        value="false"
                        checked={editSessionForm.released === false}
                        onChange={(e) => setEditSessionForm({...editSessionForm, released: e.target.value === 'true'})}
                        disabled={statusDisabled}
                        className="mr-2 text-blue-700 focus:ring-blue-500"
                      />
                      <span className={`text-sm ${statusDisabled ? 'text-gray-400' : 'text-gray-700'}`}>Ikke frigivet (kun synlig for admin)</span>
                    </label>
                  </div>
                  {editSessionForm.sessionType === 'special_event' && (
                    <p className="text-xs text-gray-500 mt-1">Special events kan ikke frigives/publiceres.</p>
                  )}
                </div>

                  <div className="flex space-x-3 mt-6">
                    <button
                      type="button"
                      onClick={resetEditSessionForm}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Annuller
                    </button>
                    <button
                      type="submit"
                      disabled={editSessionLoading}
                      className="flex-1 bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      {editSessionLoading ? 'Opdaterer...' : 'Opdater Session'}
                    </button>
                  </div>
                  {isSessionManager && (
                    <button
                      type="button"
                      onClick={handleDeleteSession}
                      className="mt-4 w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      Slet Session
                    </button>
                  )}
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Participants Modal */}
        {showParticipantsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Deltagere - {selectedSession && format(new Date(selectedSession.session_date), 'EEEE, MMMM d, yyyy')}
                </h3>
                <button
                  onClick={() => setShowParticipantsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {participantsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Indlæser deltagere...</p>
                </div>
              ) : sessionParticipants.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600">Ingen deltagere for denne session</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Medlem
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Booking Dato
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sessionParticipants.map((participant) => (
                        <tr key={participant.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{participant.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              participant.status === 'confirmed' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {participant.status === 'confirmed' ? 'Bekræftet' : 'Aflyst'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {format(new Date(participant.created_at), 'd. MMM yyyy')}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Waiting List Modal */}
        {showWaitingListModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Venteliste - {selectedSession && format(new Date(selectedSession.session_date), 'EEEE, MMMM d, yyyy')}
                </h3>
                <button
                  onClick={() => setShowWaitingListModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {waitingListLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Indlæser venteliste...</p>
                </div>
              ) : sessionWaitingList.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600">Ingen på ventelisten for denne session</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Medlem
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tilsluttet Dato
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sessionWaitingList.map((entry) => (
                        <tr key={entry.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{entry.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{entry.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {format(new Date(entry.joined_at), 'd. MMM yyyy')}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* User Management Section */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Bruger Administration</h2>
            <div className="flex gap-2">
              <button
                onClick={() => { setShowUserForm(true); setEditingUser(null); setUserForm({ name: '', email: '', password: '', phonenr: '', is_admin: false }); }}
                className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Tilføj Bruger
              </button>
              <button
                onClick={() => setShowImportModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Importér fra CSV
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            {userLoading ? (
              <div className="text-center py-8">Indlæser brugere...</div>
            ) : userError ? (
              <div className="text-red-600 px-4 py-2">{userError}</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Navn</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefon</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admin</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Oprettet</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Handlinger</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.sort((a, b) => a.name.localeCompare(b.name)).map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{user.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {user.phonenr ? (
                            <a href={`tel:${user.phonenr}`} className="text-blue-600 hover:text-blue-800 underline">
                              {user.phonenr}
                            </a>
                          ) : (
                            <span className="text-gray-500">Ingen telefon</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.is_admin ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{user.is_admin ? 'Ja' : 'Nej'}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{format(new Date(user.created_at), 'd. MMM yyyy')}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Rediger"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Slet"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* User Form Modal */}
        {showUserForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{editingUser ? 'Rediger Bruger' : 'Tilføj Bruger'}</h3>
                <button
                  onClick={resetUserForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              {userFormError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{userFormError}</div>
              )}
              {userFormSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">{userFormSuccess}</div>
              )}
              <form onSubmit={handleUserFormSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Navn</label>
                  <input
                    type="text"
                    value={userForm.name}
                    onChange={e => setUserForm({ ...userForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={e => setUserForm({ ...userForm, email: normalizeIdentifier(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Emails konverteres også til små bogstaver for at matche login-flowet.
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Telefonnummer</label>
                  <input
                    type="tel"
                    value={userForm.phonenr}
                    onChange={e => setUserForm({ ...userForm, phonenr: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="+45 12 34 56 78"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Indtast telefonnummer i internationalt format (f.eks. +45 12 34 56 78)
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Adgangskode {editingUser ? '(lad tom for at beholde nuværende)' : ''}</label>
                  <input
                    type="password"
                    value={userForm.password}
                    onChange={e => setUserForm({ ...userForm, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={editingUser ? 'Uændret hvis tom' : ''}
                    required={!editingUser}
                  />
                </div>
                <div className="mb-6">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={userForm.is_admin}
                      onChange={e => setUserForm({ ...userForm, is_admin: e.target.checked })}
                      className="form-checkbox h-5 w-5 text-blue-700"
                    />
                    <span className="ml-2 text-sm text-gray-700">Administrator</span>
                  </label>
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={resetUserForm}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Annuller
                  </button>
                  <button
                    type="submit"
                    disabled={userFormLoading}
                    className="flex-1 bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    {userFormLoading ? (editingUser ? 'Opdaterer...' : 'Opretter...') : (editingUser ? 'Opdater' : 'Opret')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Import Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Importér brugere fra CSV</h3>
                <button
                  onClick={() => { setShowImportModal(false); setImportResult(null); setImportError(''); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="mb-4">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImportCSV}
                  className="block w-full text-sm text-gray-700"
                  disabled={importLoading}
                />
                <p className="text-xs text-gray-500 mt-2">CSV skal have kolonnerne: name, email, password, phone/phonenr (valgfri), isAdmin (valgfri, true/false). Store/små bogstaver i kolonnenavne er ligegyldigt.</p>
              </div>
              {importLoading && <div className="text-blue-700 mb-2">Importerer...</div>}
              {importError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-2">{importError}</div>}
              {importResult && (
                <div className="mb-2">
                  <h4 className="font-semibold mb-1">Resultat:</h4>
                  <ul className="max-h-40 overflow-y-auto text-sm">
                    {importResult.map((r, i) => (
                      <li key={i} className={r.success ? 'text-green-700' : 'text-red-700'}>
                        {r.name || r.email}: {r.success ? 'Oprettet' : `Fejl: ${r.error}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex space-x-3 mt-4">
                <button
                  type="button"
                  onClick={() => { setShowImportModal(false); setImportResult(null); setImportError(''); }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Luk
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard; 